import type { LabelMappingEntry, LabelStrikeMapping } from '@communities/content-management/domain';
import { useStrikeConfigValidation } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { LabelWithSeveritySelector } from '@core/components/LabelWithSeveritySelector';
import type { LabelOption, LabelWithSeverity } from '@core/components/LabelWithSeveritySelector';
import type { DomainError } from '@core/domain';
import { ContentType } from '@core/graphql/types/schema';
import { Dialog, LoadingSpinner, Text, TextInput } from '@soteria/design-system-ve';
import { useCustomLabels, useStandardLabels } from '@system-config/label-management/hooks';
import { useState, useEffect, useCallback, useMemo } from 'react';

import * as styles from './LabelMappingsDialog.css';

const DRAFT_KEY_PREFIX = 'strike-label-mappings-draft-';
const MAX_MAPPINGS = 100;
const DEFAULT_POINTS = 1;

function parseDraft(draft: string): LabelStrikeMapping[] | null {
  try {
    const parsed = JSON.parse(draft) as LabelStrikeMapping[];

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

interface LabelMappingsDialogProps {
  isOpen: boolean;
  communityId: string;
  currentMappings: LabelStrikeMapping[];
  errors?: DomainError[] | null;
  onClose: () => void;
  onSave: (mappings: LabelStrikeMapping[]) => Promise<boolean>;
}

/**
 * Dialog for editing label-to-strike-point mappings.
 * Uses standard labels from the system configuration with LabelWithSeveritySelector
 * for label+severity selection and adds points input per mapping.
 */

/* eslint-disable max-statements */
export function LabelMappingsDialog({ isOpen, communityId, currentMappings, errors, onClose, onSave }: LabelMappingsDialogProps) {
  const { validateLabelMappings } = useStrikeConfigValidation();
  const { labels: standardLabels, loading: standardLoading, errors: standardErrors } = useStandardLabels();
  const { moderationLabels, loading: customLoading, errors: customErrors } = useCustomLabels(communityId);

  const draftKey = `${DRAFT_KEY_PREFIX}${communityId}`;

  const [mappings, setMappings] = useState<LabelStrikeMapping[]>(currentMappings);
  const [pointsInputs, setPointsInputs] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Combine standard and custom moderation labels into LabelOption[]
  const labelOptions: LabelOption[] = useMemo(() => {
    // Map standard labels — Image labels are presence-based (severity 1 only)
    const standard: LabelOption[] = standardLabels.map(l => ({
      id: l.id,
      labelName: l.labelName,
      maxSeverity: l.contentType === ContentType.Image ? 1 : (l.maxSeverity ?? 3),
    }));

    // Map custom moderation labels (pre-filtered by hook)
    const custom: LabelOption[] = moderationLabels.map(label => ({
      id: label.id,
      labelName: label.name,
      maxSeverity: 1, // Custom labels use single severity level
    }));

    // Sort alphabetically by label name
    return [...standard, ...custom].sort((a, b) => a.labelName.localeCompare(b.labelName));
  }, [standardLabels, moderationLabels]);

  // Build a lookup map for label info (for validation enrichment and display)
  const labelInfoMap = useMemo(() => {
    const map = new Map<string, { labelName: string; maxSeverity: number; source: 'standard' | 'custom' }>();

    // Add standard labels — Image labels are presence-based (severity 1 only)
    standardLabels.forEach(l => {
      const maxSev = l.contentType === ContentType.Image ? 1 : (l.maxSeverity ?? 3);

      map.set(l.id, { labelName: l.labelName, maxSeverity: maxSev, source: 'standard' });
    });

    // Add custom moderation labels (pre-filtered by hook)
    moderationLabels.forEach(l => {
      map.set(l.id, { labelName: l.name, maxSeverity: 1, source: 'custom' });
    });

    return map;
  }, [standardLabels, moderationLabels]);

  // Convert mappings to LabelWithSeverity[] for the selector
  const selectedLabels: LabelWithSeverity[] = useMemo(() => mappings.map(m => ({ labelId: m.labelId, severity: m.severity })), [mappings]);

  // Restore draft or reset to current value when dialog opens
  useEffect(() => {
    if (isOpen) {
      const draft = sessionStorage.getItem(draftKey);

      if (draft !== null) {
        const parsed = parseDraft(draft);

        if (parsed) {
          setMappings(parsed);
          setValidationErrors([]);

          return;
        }
      }

      setMappings(currentMappings);
      setPointsInputs({});
      setValidationErrors([]);
    }
  }, [isOpen, currentMappings, draftKey]);

  const saveDraft = useCallback(
    (updatedMappings: LabelStrikeMapping[]) => {
      sessionStorage.setItem(draftKey, JSON.stringify(updatedMappings));
    },
    [draftKey]
  );

  // Enrich mappings to LabelMappingEntry[] for validation
  const enrichMappingsForValidation = useCallback(
    (mappingsToValidate: LabelStrikeMapping[]): LabelMappingEntry[] =>
      mappingsToValidate.map(m => {
        const info = labelInfoMap.get(m.labelId);

        return {
          labelId: m.labelId,
          labelName: info?.labelName ?? `Unknown Label (${m.labelId})`,
          maxSeverity: info?.maxSeverity ?? 3,
          severity: m.severity,
          points: m.points,
          source: info?.source ?? 'standard',
        };
      }),
    [labelInfoMap]
  );

  const runValidation = useCallback(
    (updatedMappings: LabelStrikeMapping[]) => {
      if (updatedMappings.length > 0) {
        const enriched = enrichMappingsForValidation(updatedMappings);

        setValidationErrors(validateLabelMappings(enriched));
      } else {
        setValidationErrors([]);
      }
    },
    [enrichMappingsForValidation, validateLabelMappings]
  );

  // Handle LabelWithSeveritySelector onChange - sync with internal mappings
  const handleLabelsChange = useCallback(
    (updatedLabels: LabelWithSeverity[]) => {
      const updatedMappings: LabelStrikeMapping[] = updatedLabels.map(label => {
        const existing = mappings.find(m => m.labelId === label.labelId);
        const labelInfo = labelInfoMap.get(label.labelId);

        // For custom labels, force severity to 1 regardless of selector input
        const clampedSeverity = labelInfo?.source === 'custom' ? 1 : label.severity;

        return { labelId: label.labelId, severity: clampedSeverity, points: existing?.points ?? DEFAULT_POINTS };
      });

      setMappings(updatedMappings);
      saveDraft(updatedMappings);
      runValidation(updatedMappings);
    },
    [mappings, saveDraft, runValidation, labelInfoMap]
  );

  const handlePointsChange = useCallback(
    (labelId: string, value: string) => {
      // Store raw string for responsive typing UX
      setPointsInputs(prev => ({ ...prev, [labelId]: value }));

      // Only update numeric mappings when there's a valid number
      const numValue = parseInt(value, 10);

      if (!isNaN(numValue) && numValue >= 1) {
        const updatedMappings = mappings.map(m => (m.labelId === labelId ? { ...m, points: numValue } : m));

        setMappings(updatedMappings);
        saveDraft(updatedMappings);
        runValidation(updatedMappings);
      }
    },
    [mappings, saveDraft, runValidation]
  );

  // Get the display value for a points input (raw string or mapping value)
  const getPointsDisplayValue = useCallback(
    (labelId: string, points: number): string => {
      if (labelId in pointsInputs) {
        return pointsInputs[labelId];
      }

      return points.toString();
    },
    [pointsInputs]
  );

  const getLabelName = useCallback((labelId: string): string => labelInfoMap.get(labelId)?.labelName ?? labelId, [labelInfoMap]);

  const handleSubmit = useCallback(async () => {
    const enriched = enrichMappingsForValidation(mappings);
    const errs = validateLabelMappings(enriched);

    if (errs.length > 0) {
      setValidationErrors(errs);

      return;
    }

    if (mappings.length > MAX_MAPPINGS) {
      setValidationErrors([`Maximum ${MAX_MAPPINGS} label mappings allowed`]);

      return;
    }

    setIsSubmitting(true);

    try {
      const success = await onSave(mappings);

      if (success) {
        sessionStorage.removeItem(draftKey);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [mappings, draftKey, onClose, onSave, enrichMappingsForValidation, validateLabelMappings]);

  const allErrors = useMemo(() => {
    const combined = [...(errors || []), ...(standardErrors || []), ...(customErrors || [])];

    return combined.length > 0 ? combined : null;
  }, [errors, standardErrors, customErrors]);

  const isFormValid = validationErrors.length === 0;
  const labelsLoading = standardLoading || customLoading;

  return (
    <Dialog
      description="Configure which standard and custom moderation labels map to strike points."
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : 'Save',
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid || labelsLoading,
      }}
      size="lg"
      title="Edit Label Mappings"
      onClose={onClose}
    >
      <div className={styles.content}>
        {allErrors && allErrors.length > 0 && <ErrorBanner errors={allErrors} title="Error loading or saving label mappings:" />}

        {validationErrors.length > 0 && (
          <div className={styles.validationErrors} role="alert">
            {validationErrors.map(error => (
              <Text key={error} className={styles.validationError}>
                {error}
              </Text>
            ))}
          </div>
        )}

        {/* Standard Label + Severity Selector */}
        <LabelWithSeveritySelector
          availableLabels={labelOptions}
          defaultSeverity={1}
          emptyStateMessage="No label mappings configured. Add standard or custom moderation labels to define strike point values."
          loading={labelsLoading}
          selectedLabels={selectedLabels}
          title="Labels"
          onChange={handleLabelsChange}
        />

        {/* Points Input Section */}
        {mappings.length > 0 && (
          <div className={styles.pointsSection}>
            <Text className={styles.pointsSectionTitle}>Strike Points Per Label</Text>
            <Text className={styles.helperText}>Configure how many strike points each label+severity combination adds.</Text>

            {mappings.map(mapping => (
              <div key={`${mapping.labelId}-${mapping.severity}`} className={styles.pointsRow}>
                <div className={styles.pointsLabelInfo}>
                  <Text className={styles.pointsLabelName}>{getLabelName(mapping.labelId)}</Text>
                  <Text className={styles.pointsSeverity}>Severity {mapping.severity}</Text>
                </div>
                <div className={styles.pointsInput}>
                  <TextInput
                    aria-label={`Strike points for ${getLabelName(mapping.labelId)} severity ${mapping.severity}`}
                    disabled={isSubmitting}
                    label="Points"
                    min="1"
                    type="number"
                    value={getPointsDisplayValue(mapping.labelId, mapping.points)}
                    onChange={(value: string) => handlePointsChange(mapping.labelId, value)}
                  />
                </div>
              </div>
            ))}

            <Text className={styles.mappingCount}>
              {mappings.length} mapping{mappings.length !== 1 ? 's' : ''} configured (max {MAX_MAPPINGS})
            </Text>
          </div>
        )}
      </div>
    </Dialog>
  );
}
